/**
 * Calls API
 * 
 * API calls for fetching call data from Retell AI (via your backend).
 * These are the main APIs needed for the dashboard:
 * - List calls (with filtering, sorting, pagination)
 * - Get call details
 * 
 * Your backend will proxy these requests to Retell AI's API.
 */

import apiClient from './apiClient';

// ============================================================================
// LIST CALLS
// ============================================================================

/**
 * List calls with filtering, sorting, and pagination
 * 
 * This matches Retell AI's POST /v2/list-calls endpoint
 * 
 * @param {object} params - Query parameters
 * @param {string} params.location_id - Optional: Filter by restaurant location ID
 * @param {object} params.filter_criteria - Filters for calls
 * @param {string} params.sort_order - 'ascending' or 'descending' (default: 'descending')
 * @param {number} params.limit - Number of calls to return (default: 50, max: 1000)
 * @param {string} params.pagination_key - Call ID to continue from (for pagination)
 * @returns {Promise<Array>} Array of call objects
 * 
 * Filter criteria options:
 * - agent_id: string[] - Filter by agent IDs
 * - call_status: string[] - Filter by status (registered, ongoing, ended, error)
 * - call_type: string[] - Filter by type (web_call, phone_call)
 * - direction: string[] - Filter by direction (inbound, outbound)
 * - user_sentiment: string[] - Filter by sentiment (Positive, Negative, Neutral, Unknown)
 * - call_successful: boolean[] - Filter by success status
 * - start_timestamp: { upper_threshold: number, lower_threshold: number }
 * - duration_ms: { upper_threshold: number, lower_threshold: number }
 * - from_number: string[] - Filter by caller number
 * - to_number: string[] - Filter by callee number
 * 
 * @example
 * const calls = await listCalls({
 *   location_id: 'L1RNWD28M2J3M', // Filter by specific restaurant location
 *   filter_criteria: {
 *     call_status: ['ended'],
 *     start_timestamp: {
 *       lower_threshold: Date.now() - 7 * 24 * 60 * 60 * 1000, // Last 7 days
 *       upper_threshold: Date.now()
 *     }
 *   },
 *   sort_order: 'descending',
 *   limit: 50
 * });
 */
export const listCalls = async (params = {}) => {
  try {
    // Set defaults
    const requestBody = {
      filter_criteria: params.filter_criteria || {},
      sort_order: params.sort_order || 'descending',
      limit: params.limit || 1000, // Use max supported by Retell API
    };

    // Add location_id if provided (filters calls to specific restaurant location)
    if (params.location_id) {
      requestBody.location_id = params.location_id;
    }

    // Add pagination key if provided
    if (params.pagination_key) {
      requestBody.pagination_key = params.pagination_key;
    }

    // Call backend endpoint (which proxies to Retell)
    // Your backend will add the Retell API key
    console.log('📤 Sending request with limit:', requestBody.limit);
    if (params.location_id) {
      console.log('📤 Filtering by location:', params.location_id);
    }
    const response = await apiClient.post('/api/calls/list', requestBody);
    console.log('📥 Received calls count:', response.length);

    // Response should be an array of call objects
    if (!Array.isArray(response)) {
      console.error('Invalid response format:', response);
      const error = new Error('Invalid response format from server');
      error.code = 'INVALID_RESPONSE';
      throw error;
    }

    return response;
  } catch (error) {
    // Re-throw with context
    const fetchError = new Error(error.message || 'Failed to fetch calls');
    fetchError.code = error.code || 'FETCH_CALLS_ERROR';
    fetchError.status = error.status;
    throw fetchError;
  }
};

// ============================================================================
// GET CALL DETAIL
// ============================================================================

/**
 * Get detailed information about a specific call
 * 
 * Matches Retell AI's GET /v2/get-call/{call_id} endpoint
 * 
 * @param {string} callId - Call ID (e.g., "Jabr9TXYYJHfvl6Syypi88rdAHYHmcq6")
 * @returns {Promise<object>} Call object with full details
 * 
 * Response includes:
 * - Basic info (call_id, agent_id, agent_name, call_status, timestamps)
 * - Phone numbers (from_number, to_number, direction) - for phone calls
 * - Transcript (text format and structured object with timestamps)
 * - Transcript with tool calls (shows when functions were invoked)
 * - Recording URLs (single channel and multi-channel audio)
 * - Scrubbed recordings (PII removed)
 * - Public log URL (debugging info)
 * - Knowledge base retrieved contents URL
 * - Latency metrics (e2e, llm, tts, knowledge_base, s2s)
 * - Disconnection reason (why call ended)
 * - Call analysis (sentiment, summary, success status, custom data)
 * - Call cost (breakdown by product, total cost in cents)
 * - LLM token usage (if applicable)
 * - Metadata (custom data you stored)
 * - Dynamic variables (collected during call)
 * 
 * @example
 * const call = await getCallDetail('Jabr9TXYYJHfvl6Syypi88rdAHYHmcq6');
 * 
 * // Access transcript
 * console.log(call.transcript); // "Agent: hi how are you doing?\nUser: ..."
 * 
 * // Access structured transcript with timestamps
 * call.transcript_object.forEach(utterance => {
 *   console.log(`${utterance.role}: ${utterance.content}`);
 *   utterance.words.forEach(word => {
 *     console.log(`  ${word.word} (${word.start}s - ${word.end}s)`);
 *   });
 * });
 * 
 * // Access recordings
 * console.log(call.recording_url); // Single channel WAV
 * console.log(call.recording_multi_channel_url); // Multi-channel WAV
 * 
 * // Access call analysis
 * console.log(call.call_analysis.call_summary);
 * console.log(call.call_analysis.user_sentiment); // "Positive", "Negative", etc.
 * console.log(call.call_analysis.call_successful); // true/false
 * 
 * // Access latency metrics
 * console.log(call.latency.e2e.p50); // 50th percentile end-to-end latency
 * console.log(call.latency.llm.p90); // 90th percentile LLM latency
 * 
 * // Access cost
 * console.log(call.call_cost.combined_cost); // Total cost in cents
 */
export const getCallDetail = async (callId) => {
  // Validate input
  if (!callId) {
    const validationError = new Error('Call ID is required');
    validationError.code = 'VALIDATION_ERROR';
    throw validationError;
  }

  try {
    // Call backend endpoint (which proxies to Retell's GET /v2/get-call/{call_id})
    // Your backend will add the Retell API key
    const response = await apiClient.get(`/api/calls/${callId}`);

    // Validate response has required fields
    if (!response || !response.call_id) {
      const invalidError = new Error('Invalid call data received');
      invalidError.code = 'INVALID_RESPONSE';
      throw invalidError;
    }

    return response;
  } catch (error) {
    // Re-throw with context
    const callError = new Error(error.message || `Failed to fetch call ${callId}`);
    callError.code = error.code || 'FETCH_CALL_ERROR';
    callError.status = error.status;
    throw callError;
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Build filter criteria for date range
 * Helper to create timestamp filters
 * 
 * @param {Date|number} startDate - Start date
 * @param {Date|number} endDate - End date
 * @returns {object} Timestamp filter object
 * 
 * @example
 * const dateFilter = buildDateRangeFilter(
 *   new Date('2024-01-01'),
 *   new Date('2024-01-31')
 * );
 * // { lower_threshold: 1704067200000, upper_threshold: 1706659200000 }
 */
export const buildDateRangeFilter = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  // Convert to timestamps (milliseconds)
  const lower_threshold = startDate instanceof Date 
    ? startDate.getTime() 
    : startDate;
  
  const upper_threshold = endDate instanceof Date 
    ? endDate.getTime() 
    : endDate;

  return {
    lower_threshold,
    upper_threshold,
  };
};

/**
 * Get calls for a specific date range preset
 * Convenience function for common date ranges
 * 
 * @param {string} preset - Date range preset ('today', 'last_7_days', etc.)
 * @param {object} additionalFilters - Additional filter criteria
 * @returns {Promise<Array>} Array of calls
 * 
 * @example
 * const todaysCalls = await getCallsByDatePreset('today');
 * const lastWeekCalls = await getCallsByDatePreset('last_7_days', {
 *   call_status: ['ended']
 * });
 */
export const getCallsByDatePreset = async (preset, additionalFilters = {}) => {
  const now = Date.now();
  let startDate, endDate;

  switch (preset) {
    case 'today':
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate = now;
      break;

    case 'last_7_days':
      startDate = now - 7 * 24 * 60 * 60 * 1000;
      endDate = now;
      break;

    case 'last_4_weeks':
      startDate = now - 28 * 24 * 60 * 60 * 1000;
      endDate = now;
      break;

    case 'last_3_months':
      startDate = now - 90 * 24 * 60 * 60 * 1000;
      endDate = now;
      break;

    case 'week_to_date':
      startDate = new Date();
      startDate.setDate(startDate.getDate() - startDate.getDay()); // Start of week (Sunday)
      startDate.setHours(0, 0, 0, 0);
      endDate = now;
      break;

    case 'month_to_date':
      startDate = new Date();
      startDate.setDate(1); // First day of month
      startDate.setHours(0, 0, 0, 0);
      endDate = now;
      break;

    case 'year_to_date':
      startDate = new Date();
      startDate.setMonth(0, 1); // January 1st
      startDate.setHours(0, 0, 0, 0);
      endDate = now;
      break;

    case 'all_time':
      // No date filter
      return listCalls({
        filter_criteria: additionalFilters,
      });

    default:
      const presetError = new Error(`Invalid date preset: ${preset}`);
      presetError.code = 'INVALID_PRESET';
      throw presetError;
  }

  // Build filter with date range
  const filter_criteria = {
    ...additionalFilters,
    start_timestamp: buildDateRangeFilter(startDate, endDate),
  };

  return listCalls({ filter_criteria });
};

/**
 * Get calls for a specific agent
 * 
 * @param {string|string[]} agentIds - Agent ID(s)
 * @param {object} additionalFilters - Additional filters
 * @returns {Promise<Array>} Array of calls
 * 
 * @example
 * const calls = await getCallsByAgent('agent_abc123');
 * const calls = await getCallsByAgent(['agent_abc123', 'agent_def456']);
 */
export const getCallsByAgent = async (agentIds, additionalFilters = {}) => {
  // Ensure agentIds is an array
  const agentIdArray = Array.isArray(agentIds) ? agentIds : [agentIds];

  const filter_criteria = {
    ...additionalFilters,
    agent_id: agentIdArray,
  };

  return listCalls({ filter_criteria });
};

